import React, { createContext, useContext, useEffect, useState } from "react";
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  GoogleAuthProvider,
  User
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp
} from "firebase/firestore";
import firebaseConfig from "../fb.json";
import { logRefundActivity } from "../pages/cms/activityLog";

// Init Firebase (singleton)
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();

export type UserRole = "owner" | "admin" | "dev" | "editor";

export interface AppUser {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string | null;
  role: UserRole;
  projects: string[];
}

interface AuthContextType {
  user: AppUser | null;
  firebaseUser: User | null;
  loading: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  isOwner: boolean;
  isDev: boolean;
  canEdit: boolean;
  hasProject: (project: string) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load user role from Firestore
  const loadUserProfile = async (fbUser: User): Promise<AppUser | null> => {
    const userDoc = await getDoc(doc(db, "users", fbUser.uid));
    if (!userDoc.exists()) return null;

    const data = userDoc.data();
    // Update lastLoginAt
    await updateDoc(doc(db, "users", fbUser.uid), {
      lastLoginAt: serverTimestamp(),
      displayName: fbUser.displayName || data.displayName,
      photoURL: fbUser.photoURL || data.photoURL
    });

    return {
      uid: fbUser.uid,
      email: fbUser.email || data.email || "",
      displayName: fbUser.displayName || data.displayName || "",
      photoURL: fbUser.photoURL || data.photoURL || null,
      role: data.role,
      projects: data.projects || []
    };
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (!fbUser) {
        setUser(null);
        setFirebaseUser(null);
        setLoading(false);
        setError(null);
        return;
      }

      setFirebaseUser(fbUser);

      try {
        const profile = await loadUserProfile(fbUser);
        if (!profile) {
          // User not in users/ collection — no access
          await signOut(auth);
          setUser(null);
          setFirebaseUser(null);
          setError("Access denied. Contact admin for an invite.");
        } else {
          setUser(profile);
          setError(null);

          // Set session cookie
          const token = await fbUser.getIdToken();
          document.cookie = `__session=${JSON.stringify({ authToken: token })}; Path=/; Secure; SameSite=Strict`;
        }
      } catch (err: any) {
        console.error("[AuthContext] Error loading profile:", err);
        setError("Failed to load user profile.");
        setUser(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    setError(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      try { await logRefundActivity({ action: "login", uid: result.user.uid }); } catch {}
    } catch (err: any) {
      console.error("[AuthContext] Google sign-in error:", err);
      if (err.code === "auth/popup-closed-by-user") return;
      setError(err.message || "Sign-in failed.");
    }
  };

  const logout = async () => {
    await signOut(auth);
    document.cookie = "__session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    setUser(null);
    setFirebaseUser(null);
  };

  const isOwner = user?.role === "owner";
  const isAdmin = user?.role === "admin" || isOwner;
  const isDev = user?.role === "dev" || isAdmin;
  const canEdit = user?.role === "editor" || isDev;

  const hasProject = (_project: string) => !!user;

  return (
    <AuthContext.Provider
      value={{
        user,
        firebaseUser,
        loading,
        error,
        signInWithGoogle,
        logout,
        isAdmin,
        isOwner,
        isDev,
        canEdit,
        hasProject
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
